"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  ColorSwatches,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import { TimelineDiagram } from "@/components/tool-kit/diagrams/timeline-diagram";
import {
  Notice,
  PillGroup,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { type ChartColorId, colorIndex } from "@/lib/tool-kit/charts/color";
import type { NumberFormat } from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { diagramColor } from "@/lib/tool-kit/diagrams/color";
import {
  formatHistoricalDate,
  formatTick,
} from "@/lib/tool-kit/diagrams/date-format";
import {
  dateEnd,
  dateStart,
  type HistoricalDate,
} from "@/lib/tool-kit/diagrams/dates";
import {
  layoutTimeline,
  parseTimeline,
  type TimelineData,
  type TimelineOrientation,
  type TimelineProblemKind,
  type TimelineSpacing,
} from "@/lib/tool-kit/diagrams/timeline";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const TIMELINE_MAKER_STRINGS = {
  dataLabel: "Events and periods — one per line: date | label | description",
  dataHint:
    "A date is a year (1066), a year and month (1969-07) or a full date (1969-07-20). Add BCE or BC for years before the Common Era (44 BCE) and c. for an approximate date (c. 1450). A range such as 1914 – 1918 makes a period, drawn as a band. The description is optional; separate the parts with | or a tab.",
  exampleData:
    "753 BCE | Rome is founded | The traditional date, from Roman legend\n509 BCE | The Republic begins | The last king is expelled\n264 BCE | First Punic War begins | Rome's first war against Carthage\n44 BCE | Julius Caesar is assassinated | On the Ides of March\n27 BCE | Augustus becomes the first emperor | Octavian is given the title Augustus\n79 | Vesuvius erupts | Pompeii and Herculaneum are buried\n117 | The empire at its greatest extent | At the death of Trajan\n476 | The Western Roman Empire falls | Odoacer deposes Romulus Augustulus\n753 BCE – 509 BCE | Kingdom\n509 BCE – 27 BCE | Republic\n27 BCE – 476 | Empire",
  exampleTitle: "Ancient Rome, 753 BCE to 476 CE",
  eventsDetected: "one:{count} event|other:{count} events",
  periodsDetected: "one:{count} period|other:{count} periods",
  orientationLabel: "Direction",
  horizontal: "Horizontal",
  vertical: "Vertical",
  spacingLabel: "Spacing",
  proportional: "In proportion to time",
  even: "Evenly spaced",
  spacingHint:
    "In proportion to time, the gaps between dots mean something; evenly spaced keeps crowded years legible but drops the scale, so the axis has no ticks.",
  eventColor: "Color of the events",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  colorLabel: "Color",
  problem: "Line {line} was left out: {reason}",
  unreadable:
    "the date could not be read. Write a year (1066), a year and month (1969-07) or a full date (1969-07-20).",
  yearZero: "there is no year 0 — 1 BCE is followed directly by 1 CE.",
  badMonth: "the month must be from 01 to 12.",
  badDay: "that month does not have that many days.",
  endBeforeStart: "the period ends before it starts.",
  noDate: "it has no date.",
  enterData: "Enter at least one dated line to draw the timeline.",
  crowded:
    "one:With {count} event, a horizontal timeline stacks its cards high. The vertical layout gives every event its own line.|other:With {count} events, a horizontal timeline stacks its cards high. The vertical layout gives every event its own line.",
  yearBce: "{year} BCE",
  yearCe: "{year} CE",
  circa: "c. {date}",
  monthYear: "{month} {year}",
  dayMonthYear: "{monthDay}, {year}",
  dateRange: "{start} – {end}",
  dateColumn: "Date",
  labelColumn: "Event or period",
  descriptionColumn: "Description",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Timeline title",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the timeline as a table",
  chartLabel: "Timeline",
  chartDescription: "Timeline of {events}, from {first} to {last}.",
  periodsDescription: "Periods: {periods}.",
} as const;

type Strings = typeof TIMELINE_MAKER_STRINGS;

const PROBLEM_KEYS: Record<
  TimelineProblemKind,
  | "unreadable"
  | "yearZero"
  | "badMonth"
  | "badDay"
  | "endBeforeStart"
  | "noDate"
> = {
  unreadable: "unreadable",
  yearZero: "yearZero",
  badMonth: "badMonth",
  badDay: "badDay",
  endBeforeStart: "endBeforeStart",
  noDate: "noDate",
};

/** Every line of the timeline in date order, as table rows. */
function timelineRows(
  data: TimelineData,
  date: (value: HistoricalDate) => string,
  strings: Strings
): { start: number; cells: string[] }[] {
  return [
    ...data.events.map((event) => ({
      start: event.day,
      cells: [date(event.date), event.label, event.description],
    })),
    ...data.periods.map((period) => ({
      start: period.startDay,
      cells: [
        t(strings.dateRange, {
          start: date(period.start),
          end: date(period.end),
        }),
        period.label,
        "",
      ],
    })),
  ].sort((a, b) => a.start - b.start);
}

function describe(
  data: TimelineData,
  date: (value: HistoricalDate) => string,
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string {
  const first = data.events[0];
  const last = data.events.at(-1);
  const parts: string[] = [];
  if (first && last) {
    parts.push(
      t(strings.chartDescription, {
        events: tPlural(strings.eventsDetected, locale, data.events.length, {
          count: fmt(data.events.length),
        }),
        first: date(first.date),
        last: date(last.date),
      })
    );
  }
  if (data.periods.length > 0) {
    parts.push(
      t(strings.periodsDescription, {
        periods: data.periods
          .map(
            (period) =>
              `${period.label} (${t(strings.dateRange, {
                start: date(period.start),
                end: date(period.end),
              })})`
          )
          .join(", "),
      })
    );
  }
  return parts.join(" ");
}

export default function TimelineMakerWidget() {
  const strings = useToolUi(TIMELINE_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [orientation, setOrientation] =
    useState<TimelineOrientation>("horizontal");
  const [spacing, setSpacing] = useState<TimelineSpacing>("proportional");
  const [accentId, setAccentId] = useState<ChartColorId>("blue");
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const data = parseTimeline(raw);
  const showEra =
    data.events.some((event) => event.date.bce) ||
    data.periods.some((period) => period.start.bce || period.end.bce);
  const date = (value: HistoricalDate) =>
    formatHistoricalDate(value, showEra, locale, strings, fmt);
  const accent = CHART_COLORS[colorIndex(accentId)];
  const hasContent = data.events.length + data.periods.length > 0;
  const layout = hasContent
    ? layoutTimeline({
        events: data.events.map((event) => ({
          day: event.day,
          circa: event.date.circa,
          text: {
            date: date(event.date),
            label: event.label,
            description: event.description,
          },
        })),
        periods: data.periods.map((period) => ({
          start: period.startDay,
          end: period.endDay,
          // An overlap no longer than the end's own unit is a shared boundary year.
          slack: dateEnd(period.end) - dateStart(period.end),
          label: period.label,
        })),
        orientation,
        spacing,
        title: style.apa ? "" : style.title,
        tickLabel: (tick, index) =>
          formatTick(tick, index, showEra, locale, strings, fmt),
      })
    : null;
  const rows = timelineRows(data, date, strings);
  const head = [
    strings.dateColumn,
    strings.labelColumn,
    strings.descriptionColumn,
  ];

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="tlm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        prose
        rows={10}
        value={raw}
      />
      <div aria-live="polite" className="space-y-2">
        {hasContent ? (
          <p className={HINT}>
            {tPlural(strings.eventsDetected, locale, data.events.length, {
              count: fmt(data.events.length),
            })}
            {" · "}
            {tPlural(strings.periodsDetected, locale, data.periods.length, {
              count: fmt(data.periods.length),
            })}
          </p>
        ) : (
          <Notice>{strings.enterData}</Notice>
        )}
        {data.problems.map((problem) => (
          <Notice key={`${problem.line}-${problem.kind}`}>
            {t(strings.problem, {
              line: fmt(problem.line),
              reason: strings[PROBLEM_KEYS[problem.kind]],
            })}
          </Notice>
        ))}
        {orientation === "horizontal" && data.events.length > 12 ? (
          <Notice>
            {tPlural(strings.crowded, locale, data.events.length, {
              count: fmt(data.events.length),
            })}
          </Notice>
        ) : null}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.orientationLabel}
          name="tlm-orientation"
          onChange={setOrientation}
          options={[
            { value: "horizontal", label: strings.horizontal },
            { value: "vertical", label: strings.vertical },
          ]}
          value={orientation}
        />
        <div className="space-y-2">
          <PillGroup
            label={strings.spacingLabel}
            name="tlm-spacing"
            onChange={setSpacing}
            options={[
              { value: "proportional", label: strings.proportional },
              { value: "even", label: strings.even },
            ]}
            value={spacing}
          />
          <p className={HINT}>{strings.spacingHint}</p>
        </div>
      </div>
      {layout ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([head, ...rows.map((row) => row.cells)])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "timeline")}
          labels={strings}
          note={style.note}
          table={{ head, rows: rows.map((row) => row.cells) }}
          title={style.title}
        >
          <TimelineDiagram
            accent={accent}
            bandColors={data.periods.map((_, index) =>
              diagramColor(colorIndex(accentId) + 1, index)
            )}
            description={describe(data, date, strings, locale, fmt)}
            id="tlm-timeline"
            label={style.title.trim() || strings.chartLabel}
            layout={layout}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        idPrefix="tlm"
        labels={strings}
        style={style}
        update={update}
      >
        <ColorSwatches
          labels={strings}
          legend={strings.eventColor}
          name="tlm-color"
          onChange={setAccentId}
          value={accentId}
        />
      </ChartStyleFields>
    </div>
  );
}
